import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import NumberInput from '~ComponentsImproved/Numpad';
import Button from '~/Components/Button';
import { useI18n } from '~Hooks';
import { Item } from './CreateOrder';
import { useSwipeable } from 'react-swipeable';

const Wrapper = styled.div`
  display: flex;
  box-sizing: border-box;
  margin-bottom: auto;
  align-items: flex-start;
  justify-content: center;
`;

const ListWrapper = styled.div`
  width: 368px;
  margin-right: 16px;
`;

const Table = styled.div`
  flex: 1;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  background-color: #fff;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  box-sizing: border-box;
  margin-bottom: 16px;
  color: #1c232c;
  max-height: 503px;
`;

const TableHeader = styled.div`
  display: flex;
  background-color: #fbfbfb;
  align-items: center;
  border-bottom: 1px solid #e8e9ea;
`;

const Body = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding-bottom: 8px;
  height: 613px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Row = styled.div<{ selected: boolean }>`
  background-color: ${({ selected }) => (selected ? '#545960' : '#fff')};
  color: ${({ selected }) => (selected ? '#ffffff' : '#1c232c')};
  width: 100%;
  display: flex;
  box-sizing: border-box;
  position: relative;
  z-index: ${(props) => (props.selected ? '1' : '0')};
  transition: box-shadow 2s ease-in-out;
  box-shadow: ${(props) => (props.selected ? '0px 1px 3px 0px rgba(0, 0, 0, 0.24)' : 'none')};
  border-bottom: 2px solid ${(props) => (props.selected ? '#545960' : '#e8e9ea')};
  margin-top: -1px;
  transition: border-color 0.2s ease-in-out;

  div {
    font-weight: ${(props) => (props.selected ? '600' : '400')};
  }
`;

const Title = styled.div`
  flex: 1;
  color: #41474f;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 20px;
  padding: 18px 24px;
  box-sizing: border-box;
`;

const QtyTitle = styled.div`
  width: 116px;
  text-align: center;
  color: #41474f;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 20px;
  padding: 18px 8px;
  box-sizing: border-box;
`;

const Product = styled.div`
  flex: 1;
  padding: 13px 24px;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.24px;
  line-height: 20px;
  box-sizing: border-box;
  word-break: break-word;
`;

const Quantity = styled.div`
  padding: 13px 8px;
  width: 116px;
  box-sizing: border-box;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  transition: border-color 0.2s ease-in-out;
`;

const Loading = styled.div`
  padding: 13px 24px;
`;
interface Props {
  inventoryItems: Item[];
  setInventoryItems: (val: Item[]) => void;
  loading: boolean;
  handleSubmit: () => void;
}

const InventoryList: React.FunctionComponent<Props> = ({
  inventoryItems,
  setInventoryItems,
  loading,
  handleSubmit,
}) => {
  const { i18n } = useI18n();
  const [touching, setTouching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(0);
  const rowRefs = useRef<any[]>([]);

  const handlers = useSwipeable({
    delta: 10,
    onSwiping: () => setTouching(false),
  });

  const changeAmount = (count: number) => {
    const updatedProducts = [...inventoryItems];
    updatedProducts[selectedProduct].count = count;
    setInventoryItems(updatedProducts);

    if (selectedProduct < inventoryItems.length - 1) setSelectedProduct(selectedProduct + 1);
  };

  useEffect(() => {
    if (rowRefs.current[selectedProduct]) {
      rowRefs.current[selectedProduct].scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedProduct]);

  const handleSelect = (item: any) => {
    if (touching) {
      setSelectedProduct(item);
    }
    setTimeout(() => {
      setTouching(false);
    }, 300);
  };

  return (
    <Wrapper>
      <ListWrapper>
        <Table>
          <TableHeader>
            <Title>{i18n('Inventory.Product')}</Title>
            <QtyTitle>{i18n('EOD.Quantity')}</QtyTitle>
          </TableHeader>
          <Body {...handlers}>
            {loading ? (
              <Loading>{i18n('General.Loading')}</Loading>
            ) : (
              inventoryItems.map((item, i) => {
                return (
                  <Row
                    ref={(el) => (rowRefs.current[i] = el)}
                    selected={selectedProduct == i}
                    key={i}
                    onTouchStart={() => setTouching(true)}
                    onTouchEnd={() => handleSelect(i)}>
                    <Product>{item.name}</Product>
                    <Quantity>{item.count}</Quantity>
                  </Row>
                );
              })
            )}
          </Body>
        </Table>
        <Button disabled={loading} handleClick={handleSubmit} text={i18n('Inventory.Order')} />
      </ListWrapper>
      <NumberInput
        notModal
        maxValue={10000}
        initialValue={'0'}
        onSubmit={(amount) => changeAmount(Number(amount))}
      />
    </Wrapper>
  );
};

export default InventoryList;
